import { useMutation } from "@tanstack/react-query";
import { API_CONFIG } from "@/utils/config";

// ============================================
// 서비스 신청 타입 정의
// ============================================
export type ServiceType = "messaging" | "aicc";
export type BusinessType = "general" | "special" | "reseller";

export interface ServiceSubmitFormData {
  contactName: string;
  deptName: string;
  jobTitle: string;
  contactPhone: string;
  corporateName: string;
  industry: string;
  bizRegNo: string;
  contactEmail: string;
  zipCode: string;
  address: string;
  addressDetail: string;
  purposeOther: string;
  companyTypeOther: string;
  agentServerIp: string;
  webhookUrl: string;
  sdkDomain: string;
}

export interface ServiceSubmitPayload {
  serviceType: ServiceType;
  businessType: BusinessType;
  formData: ServiceSubmitFormData;
  purposes: string[];
  companyTypes: string[];
  files: Record<string, File | null>;
  serviceKind: string;
}

export interface ServiceSubmitResponse {
  requestId: number;
  createAt: string;
  [key: string]: unknown;
}

// ============================================
// API 함수
// ============================================
const submitServiceApplication = async (
  payload: ServiceSubmitPayload
): Promise<ServiceSubmitResponse> => {
  const { serviceType, businessType, formData, purposes, companyTypes, files, serviceKind } =
    payload;

  // FormData 구성
  const fd = new FormData();

  // webhookUrl 파싱 (IP:PORT 형식)
  let webhookIp = "";
  let webhookPort = "";
  if (formData.webhookUrl) {
    try {
      const url = new URL(
        formData.webhookUrl.startsWith("http")
          ? formData.webhookUrl
          : `http://${formData.webhookUrl}`
      );
      webhookIp = url.hostname;
      webhookPort = url.port || "80";
    } catch {
      // URL 파싱 실패 시 정규식으로 파싱
      const match = formData.webhookUrl.match(/^(?:https?:\/\/)?([^:]+):(\d+)/);
      if (match) {
        webhookIp = match[1];
        webhookPort = match[2];
      }
    }
  }

  // data 객체 구성 (formData를 기반으로 필요한 필드 추가)
  const dataObject: Record<string, unknown> = {
    ...formData,
    corpType: `${serviceType}-${businessType}`, // messaging-general, messaging-special, aicc-general, aicc-reseller
    status: "pending", // 초기 상태
    writerName: formData.contactName, // 작성자명은 담당자명과 동일
    title: `${formData.corporateName} - ${serviceType === "messaging" ? "메시징 서비스" : "문자콜센터"} 신청`,
    serviceKind: serviceKind, // 서비스 종류 (메시징-일반, 메시징-특수, AICC-일반, AICC-재판매사)
  };

  // 메시징 서비스 전용 필드
  if (serviceType === "messaging") {
    dataObject.vendorType = companyTypes; // List<String> 형태로 전송
    dataObject.msgPurpose = purposes; // List<String> 형태로 전송
    dataObject.senderType = businessType;
    dataObject.webhookIp = webhookIp;
    dataObject.webhookPort = webhookPort ? parseInt(webhookPort) : null;
  }

  // data를 JSON 문자열로 변환하여 @RequestPart("data")에 전달
  const dataBlob = new Blob([JSON.stringify(dataObject)], { type: "application/json" });
  fd.append("data", dataBlob);

  // 파일 첨부 (MultiValueMap<String, MultipartFile> fileMap 형태)
  // 각 파일 필드를 file_code 형식으로 전송
  Object.entries(files).forEach(([key, file]) => {
    if (file) {
      fd.append(key, file);
    }
  });

  // API 요청
  const response = await fetch(`${API_CONFIG.VOC_BASE_URL}/serviceRequest/${serviceType}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
    },
    body: fd,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`접수 실패: ${errorText}`);
  }

  const data = await response.json();
  if (!data || !data.requestId) {
    throw new Error("접수 결과 오류");
  }

  return data;
};

// ============================================
// useMutation 훅
// ============================================
export const useServiceSubmit = () => {
  return useMutation({
    mutationFn: submitServiceApplication,
  });
};
